import { MutableRefObject, RefCallback, RefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react';

// This is the order the items will be rendered in the header, and thus the
// order they will be evaluated for visibility based on available space.
// The priority of each item is determined by a combination of this order
// and the explicit priority assigned to each item in the metadata
// (see `HEADER_MENU_ITEM_METADATA`).
// Keep mobile primary link source order in MobileLinksList.tsx aligned with
// this list where possible (except explicit mobile-only ordering overrides).
export const HEADER_MENU_ITEM_IDS = [
  'updates',
  'films',
  'photos',
  'models',
  'discounts',
  'partners',
  'liveGirls',
  'blog',
  'categories',
  'join',
  'login',
  'search',
  'activity',
  'settings',
  'userMenu',
] as const;

export type HeaderMenuItemId = typeof HEADER_MENU_ITEM_IDS[number];

export type HeaderMenuEligibilityKey =
  | 'always'
  | 'user'
  | 'join'
  | 'login'
  | 'userValidSubscription'
  | 'config.hasBlogWithoutSeoCategoriesMenu'
  | 'config.hasUpdates'
  | 'config.movies.active'
  | 'featureFlag.enableSeoCategoriesMenu';

type HeaderMenuItemMetadata = {
  priority: number;
  eligibilityKey: HeaderMenuEligibilityKey;
};

// Enforces a metadata entry for every menu item key in `HEADER_MENU_ITEM_IDS`.
type HeaderMenuItemMetadataById = {
  [K in HeaderMenuItemId]: HeaderMenuItemMetadata;
};

// Lower priority items will be hidden before higher priority ones when space
// is limited. Items with the same priority will fall back to the order defined
// in `HEADER_MENU_ITEM_IDS`.
// These were set by Shaun
export const HEADER_MENU_ITEM_METADATA: HeaderMenuItemMetadataById = {
  activity: { priority: 1, eligibilityKey: 'user' },
  settings: { priority: 2, eligibilityKey: 'user' },
  blog: { priority: 3, eligibilityKey: 'config.hasBlogWithoutSeoCategoriesMenu' },
  categories: { priority: 3, eligibilityKey: 'featureFlag.enableSeoCategoriesMenu' },
  photos: { priority: 4, eligibilityKey: 'always' },
  models: { priority: 5, eligibilityKey: 'always' },
  films: { priority: 6, eligibilityKey: 'config.movies.active' },
  updates: { priority: 7, eligibilityKey: 'config.hasUpdates' },
  search: { priority: 8, eligibilityKey: 'always' },
  partners: { priority: 9, eligibilityKey: 'user' },
  liveGirls: { priority: 10, eligibilityKey: 'always' },
  discounts: { priority: 11, eligibilityKey: 'user' },
  login: { priority: 18, eligibilityKey: 'login' },
  join: { priority: 19, eligibilityKey: 'join' },
  userMenu: { priority: 20, eligibilityKey: 'user' },
};

export type HeaderMenuItemEligibility = Partial<Record<HeaderMenuItemId, boolean>>;

export type HeaderMenuEligibilityContext = {
  hasUser: boolean;
  userValidSubscription: boolean;
  configHasBlog: boolean;
  configHasUpdates: boolean;
  configMoviesActive: boolean;
  configMoviesRequiresAuth: boolean;
  enableSeoCategoriesMenu: boolean;
};

type UseHeaderMenuItemVisibilityArgs = {
  eligibilityContext?: HeaderMenuEligibilityContext;
  eligibleItems?: HeaderMenuItemEligibility;
  menuItemsContainerRef?: RefObject<HTMLElement>;
  includeDebugData?: boolean;
};

type HeaderMenuVisibilityDebugData = {
  availableWidth?: number;
  allWidthsMeasured: boolean;
  overflow: boolean;
  eligibleItemIds: HeaderMenuItemId[];
  requiredWidth: number;
  visibleItemIds: HeaderMenuItemId[];
  hiddenItemIds: HeaderMenuItemId[];
  measuredWidths: Partial<Record<HeaderMenuItemId, number>>;
};

type UseHeaderMenuItemVisibilityResult = {
  // Includes both eligibility and overflow-priority decisions.
  isVisible: (itemId: HeaderMenuItemId) => boolean;
  isVisibilityResolved: boolean;
  registerItem: (itemId: HeaderMenuItemId) => RefCallback<HTMLElement>;
  debugData?: HeaderMenuVisibilityDebugData;
};

export type HeaderMenuItemVisibilityChecker = UseHeaderMenuItemVisibilityResult['isVisible'];
export type HeaderMenuItemRegister = UseHeaderMenuItemVisibilityResult['registerItem'];
export type HeaderMenuItemWidths = Partial<Record<HeaderMenuItemId, number>>;

const cancelScheduledRecalculate = (resizeRafRef: MutableRefObject<number | null>) => {
  if (typeof window !== 'undefined' && resizeRafRef.current !== null) {
    window.cancelAnimationFrame(resizeRafRef.current);
    resizeRafRef.current = null;
  }
};

const observeEligibleItemElements = (
  observer: ResizeObserver,
  eligibleItemIds: HeaderMenuItemId[],
  itemElementsRef: MutableRefObject<Partial<Record<HeaderMenuItemId, HTMLElement | null>>>,
) => {
  for (const itemId of eligibleItemIds) {
    const element = itemElementsRef.current[itemId];
    if (element) {
      observer.observe(element);
    }
  }
};

const syncObservedItemElement = ({
  element,
  itemId,
  itemElementsRef,
  resizeObserverRef,
  scheduleRecalculate,
}: {
  element: HTMLElement | null;
  itemId: HeaderMenuItemId;
  itemElementsRef: MutableRefObject<Partial<Record<HeaderMenuItemId, HTMLElement | null>>>;
  resizeObserverRef: MutableRefObject<ResizeObserver | null>;
  scheduleRecalculate: () => void;
}) => {
  if (itemElementsRef.current[itemId] === element) {
    return;
  }

  const previousElement = itemElementsRef.current[itemId];
  if (previousElement && resizeObserverRef.current) {
    resizeObserverRef.current.unobserve(previousElement);
  }

  itemElementsRef.current[itemId] = element;

  if (element && resizeObserverRef.current) {
    resizeObserverRef.current.observe(element);
  }

  scheduleRecalculate();
};

const HEADER_MENU_ITEM_ORDER_INDEX: Record<HeaderMenuItemId, number> = HEADER_MENU_ITEM_IDS.reduce(
  (acc, itemId, index) => {
    acc[itemId] = index;
    return acc;
  },
  {} as Record<HeaderMenuItemId, number>,
);

const sortByPriorityThenOrder = (left: HeaderMenuItemId, right: HeaderMenuItemId) => {
  const leftMeta = HEADER_MENU_ITEM_METADATA[left];
  const rightMeta = HEADER_MENU_ITEM_METADATA[right];

  // Lower numeric priority is removed first; ties use HEADER_MENU_ITEM_IDS order.
  if (leftMeta.priority === rightMeta.priority) {
    return HEADER_MENU_ITEM_ORDER_INDEX[left] - HEADER_MENU_ITEM_ORDER_INDEX[right];
  }

  return leftMeta.priority - rightMeta.priority;
};

export const getSortedEligibleHeaderMenuItemIds = (eligibleItems?: HeaderMenuItemEligibility): HeaderMenuItemId[] => {
  return HEADER_MENU_ITEM_IDS.filter((itemId) => eligibleItems?.[itemId] ?? true).sort(sortByPriorityThenOrder);
};

const areItemIdsEqual = (left: HeaderMenuItemId[], right: HeaderMenuItemId[]) => {
  if (left.length !== right.length) {
    return false;
  }

  for (let i = 0; i < left.length; i += 1) {
    if (left[i] !== right[i]) {
      return false;
    }
  }

  return true;
};

export const calculateVisibleHeaderMenuItemIds = ({
  allWidthsMeasured,
  availableWidth,
  eligibleItemIds,
  measuredWidths,
}: {
  allWidthsMeasured: boolean;
  availableWidth: number;
  eligibleItemIds: HeaderMenuItemId[];
  measuredWidths: HeaderMenuItemWidths;
}): HeaderMenuItemId[] => {
  if (!allWidthsMeasured) {
    // Keep all eligible items mounted until the first full measurement pass.
    // Callers can hide the container during this phase to avoid visual flashing.
    return eligibleItemIds;
  }

  const requiredWidth = Object.values(measuredWidths).reduce((total, width) => total + width, 0);

  if (requiredWidth <= availableWidth) {
    return eligibleItemIds;
  }

  let runningWidth = requiredWidth;
  const hiddenSet = new Set<HeaderMenuItemId>();

  for (const itemId of eligibleItemIds) {
    if (runningWidth <= availableWidth) {
      break;
    }

    hiddenSet.add(itemId);
    runningWidth -= measuredWidths[itemId] ?? 0;
  }

  return eligibleItemIds.filter((itemId) => !hiddenSet.has(itemId));
};

const resolveEligibilityKey = (key: HeaderMenuEligibilityKey, context: HeaderMenuEligibilityContext): boolean => {
  switch (key) {
    case 'always':
      return true;
    case 'user':
      return context.hasUser;
    case 'join':
      // "join" is a grouped slot: logged-out GET STARTED, plus free-member JOIN NOW.
      return !context.hasUser || (context.hasUser && !context.userValidSubscription);
    case 'login':
      return !context.hasUser;
    case 'userValidSubscription':
      return context.hasUser && context.userValidSubscription;
    case 'config.hasBlogWithoutSeoCategoriesMenu':
      return context.configHasBlog && !context.enableSeoCategoriesMenu;
    case 'config.hasUpdates':
      return context.configHasUpdates;
    case 'config.movies.active':
      // If movies requires auth, only show when a user exists.
      return context.configMoviesActive && (!context.configMoviesRequiresAuth || context.hasUser);
    case 'featureFlag.enableSeoCategoriesMenu':
      return context.enableSeoCategoriesMenu;
    default:
      return true;
  }
};

export const isHeaderMenuItemEligible = (
  itemId: HeaderMenuItemId,
  context: HeaderMenuEligibilityContext,
  overrides: HeaderMenuItemEligibility = {},
): boolean => {
  // hasOwnProperty lets explicit "false" overrides win over computed defaults.
  const hasOverride = Object.prototype.hasOwnProperty.call(overrides, itemId);

  if (hasOverride) {
    return Boolean(overrides[itemId]);
  }

  return resolveEligibilityKey(HEADER_MENU_ITEM_METADATA[itemId].eligibilityKey, context);
};

export const getHeaderMenuItemEligibility = (
  context: HeaderMenuEligibilityContext,
  overrides: HeaderMenuItemEligibility = {},
): HeaderMenuItemEligibility => {
  return HEADER_MENU_ITEM_IDS.reduce<HeaderMenuItemEligibility>((acc, itemId) => {
    acc[itemId] = isHeaderMenuItemEligible(itemId, context, overrides);
    return acc;
  }, {});
};

export const useHeaderMenuItemVisibility = (
  args: UseHeaderMenuItemVisibilityArgs = {},
): UseHeaderMenuItemVisibilityResult => {
  const { eligibilityContext, eligibleItems, menuItemsContainerRef, includeDebugData = false } = args;
  const itemElementsRef = useRef<Partial<Record<HeaderMenuItemId, HTMLElement | null>>>({});
  const measuredWidthsRef = useRef<Partial<Record<HeaderMenuItemId, number>>>({});
  const registerCallbacksRef = useRef<Partial<Record<HeaderMenuItemId, RefCallback<HTMLElement>>>>({});
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const resizeRafRef = useRef<number | null>(null);
  const scheduleRecalculateRef = useRef<() => void>(() => {});

  const resolvedEligibleItems = useMemo(() => {
    if (!eligibilityContext) {
      return eligibleItems;
    }

    return getHeaderMenuItemEligibility(eligibilityContext, eligibleItems);
  }, [eligibilityContext, eligibleItems]);

  const eligibleItemIds = useMemo(() => getSortedEligibleHeaderMenuItemIds(resolvedEligibleItems), [
    resolvedEligibleItems,
  ]);
  const eligibleItemIdsKey = useMemo(() => eligibleItemIds.join('|'), [eligibleItemIds]);

  const [visibleItemIds, setVisibleItemIds] = useState<HeaderMenuItemId[]>(eligibleItemIds);
  const [visibilityResolvedForEligibleKey, setVisibilityResolvedForEligibleKey] = useState<string | null>(null);

  const visibleSet = useMemo(() => new Set(visibleItemIds), [visibleItemIds]);
  const isVisibilityResolved = visibilityResolvedForEligibleKey === eligibleItemIdsKey;

  const measureState = useCallback(() => {
    const availableWidth = menuItemsContainerRef?.current?.getBoundingClientRect().width ?? 0;

    const measuredWidths = eligibleItemIds.reduce<Partial<Record<HeaderMenuItemId, number>>>((acc, itemId) => {
      const element = itemElementsRef.current[itemId];
      const liveWidth = element?.getBoundingClientRect().width;
      const cachedWidth = measuredWidthsRef.current[itemId];

      // 0px is still a valid measurement (e.g. item hidden by responsive CSS),
      // so only treat as "missing" when we have neither live nor cached width.
      if (typeof liveWidth === 'number') {
        acc[itemId] = liveWidth;
        measuredWidthsRef.current[itemId] = liveWidth;
        return acc;
      }

      if (typeof cachedWidth === 'number') {
        acc[itemId] = cachedWidth;
      }

      return acc;
    }, {});

    const requiredWidth = Object.values(measuredWidths).reduce((total, width) => total + width, 0);
    const allWidthsMeasured = eligibleItemIds.every((itemId) =>
      Object.prototype.hasOwnProperty.call(measuredWidths, itemId),
    );

    return {
      availableWidth,
      allWidthsMeasured,
      measuredWidths,
      requiredWidth,
      overflow: requiredWidth > availableWidth,
    };
  }, [eligibleItemIds, menuItemsContainerRef]);

  const recalculateVisibility = useCallback(() => {
    const measurement = measureState();
    const { allWidthsMeasured, availableWidth, measuredWidths } = measurement;
    const nextVisibleItemIds = calculateVisibleHeaderMenuItemIds({
      allWidthsMeasured,
      availableWidth,
      eligibleItemIds,
      measuredWidths,
    });

    setVisibleItemIds((currentVisibleItemIds) => {
      if (areItemIdsEqual(currentVisibleItemIds, nextVisibleItemIds)) {
        return currentVisibleItemIds;
      }

      return nextVisibleItemIds;
    });
    setVisibilityResolvedForEligibleKey((currentResolvedKey) => {
      if (!allWidthsMeasured) {
        return currentResolvedKey === null ? currentResolvedKey : null;
      }

      return currentResolvedKey === eligibleItemIdsKey ? currentResolvedKey : eligibleItemIdsKey;
    });
  }, [eligibleItemIds, eligibleItemIdsKey, measureState]);

  const scheduleRecalculate = useCallback(() => {
    if (typeof window === 'undefined') {
      recalculateVisibility();
      return;
    }

    if (resizeRafRef.current !== null) {
      return;
    }

    // Coalesce bursty resize/observer events into one layout read per frame.
    resizeRafRef.current = window.requestAnimationFrame(() => {
      resizeRafRef.current = null;
      recalculateVisibility();
    });
  }, [recalculateVisibility]);

  // Observer callbacks can outlive a render; keep the latest scheduler in a ref.
  scheduleRecalculateRef.current = scheduleRecalculate;

  const registerItem = (itemId: HeaderMenuItemId) => {
    const existingCallback = registerCallbacksRef.current[itemId];

    // Keep callback refs stable per item so React doesn't detach/reattach refs each render.
    if (existingCallback) {
      return existingCallback;
    }

    const callback: RefCallback<HTMLElement> = (element) => {
      syncObservedItemElement({
        element,
        itemId,
        itemElementsRef,
        resizeObserverRef,
        scheduleRecalculate: scheduleRecalculateRef.current,
      });
    };

    registerCallbacksRef.current[itemId] = callback;
    return callback;
  };

  useEffect(() => {
    scheduleRecalculate();
    const container = menuItemsContainerRef?.current;

    if (!container || typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', scheduleRecalculate);

      return () => {
        window.removeEventListener('resize', scheduleRecalculate);
        cancelScheduledRecalculate(resizeRafRef);
      };
    }

    const observer = new ResizeObserver(() => {
      scheduleRecalculateRef.current();
    });
    resizeObserverRef.current = observer;
    observer.observe(container);
    observeEligibleItemElements(observer, eligibleItemIds, itemElementsRef);
    // Keep window resize as a backstop because some viewport changes are not
    // guaranteed to emit observer callbacks for every observed node.
    window.addEventListener('resize', scheduleRecalculate);

    return () => {
      resizeObserverRef.current = null;
      observer.disconnect();
      window.removeEventListener('resize', scheduleRecalculate);
      cancelScheduledRecalculate(resizeRafRef);
    };
  }, [eligibleItemIds, menuItemsContainerRef, scheduleRecalculate]);

  const debugData = useMemo<HeaderMenuVisibilityDebugData | undefined>(() => {
    if (!includeDebugData) {
      return undefined;
    }

    const { allWidthsMeasured, availableWidth, measuredWidths, overflow, requiredWidth } = measureState();

    return {
      availableWidth,
      allWidthsMeasured,
      eligibleItemIds,
      visibleItemIds,
      overflow,
      requiredWidth,
      hiddenItemIds: eligibleItemIds.filter((itemId) => !visibleSet.has(itemId)),
      measuredWidths,
    };
  }, [eligibleItemIds, includeDebugData, measureState, visibleItemIds, visibleSet]);

  return {
    isVisible: (itemId) => visibleSet.has(itemId),
    isVisibilityResolved,
    registerItem,
    debugData,
  };
};
